/**
 * Maintenance API Layer.
 *
 * React Query hooks for service provider CRUD and contacts.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ServiceProvider {
  id: string;
  organization_id: string;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, unknown> | null;
  website: string | null;
  tax_id: string | null;
  payment_terms: string | null;
  specializations: string[] | null;
  hourly_rate: number | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  contacts: ServiceProviderContact[];
}

export interface ServiceProviderContact {
  id: string;
  service_provider_id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Service Provider Hooks
// ---------------------------------------------------------------------------

export function useServiceProviders(
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
  } = {},
) {
  const { page = 1, pageSize = 100, search } = params;
  return useQuery<PaginatedResponse<ServiceProvider>>({
    queryKey: ["service-providers", { page, pageSize, search }],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("page", String(page));
      sp.set("page_size", String(pageSize));
      if (search) sp.set("search", search);
      return (await apiClient.get(`/api/v1/service-providers?${sp}`))
        .data as PaginatedResponse<ServiceProvider>;
    },
  });
}

export function useServiceProvider(id: string | undefined) {
  return useQuery<ServiceProvider>({
    queryKey: ["service-providers", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/service-providers/${id}`))
        .data as ServiceProvider,
    enabled: !!id,
  });
}

export function useCreateServiceProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/service-providers", data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-providers"] }),
  });
}

export function useUpdateServiceProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, unknown>;
    }) => (await apiClient.put(`/api/v1/service-providers/${id}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-providers"] }),
  });
}

export function useDeleteServiceProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/service-providers/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["service-providers"] }),
  });
}

// ---------------------------------------------------------------------------
// Contact Hooks
// ---------------------------------------------------------------------------

export function useAddServiceProviderContact(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (
        await apiClient.post(
          `/api/v1/service-providers/${providerId}/contacts`,
          data,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["service-providers", providerId] }),
  });
}

export function useUpdateServiceProviderContact(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contactId,
      data,
    }: {
      contactId: string;
      data: Record<string, unknown>;
    }) =>
      (
        await apiClient.put(
          `/api/v1/service-providers/${providerId}/contacts/${contactId}`,
          data,
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["service-providers", providerId] }),
  });
}

export function useDeleteServiceProviderContact(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      await apiClient.delete(
        `/api/v1/service-providers/${providerId}/contacts/${contactId}`,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["service-providers", providerId] }),
  });
}
